// --- Award categories -----------------------------------------------------------------------

/** One row of the award categories list. */
export interface AwardCategoryListItem {
  id: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  /** The live documents on the checklist. */
  documentCount: number;
  /** Live awards of this category. */
  awardCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** One checklist line — a document every nominee under the category submits. */
export interface AwardCategoryDocument {
  id: string;
  name: string;
  description?: string | null;
  /** The points a NEW award starts this document's criterion at. Absent where none is set — read with `!= null`. */
  defaultMaxPoints?: number | null;
  sortOrder: number;
}

export interface AwardCategoryDetail {
  id: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  /** The live checklist, in order. */
  documents: AwardCategoryDocument[];
  awardCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * One checklist line on the wire. `id` names a line already on the checklist so the nominees
 * registered against it keep pointing at it; null adds one. Every row on screen is sent, blanks
 * included, so a rejection's index is the row the reader sees.
 */
export interface AwardCategoryDocumentInput {
  id: string | null;
  name: string;
  description: string | null;
  defaultMaxPoints: number | null;
}

export interface AwardCategoryRequest {
  name: string;
  description: string | null;
  documents: AwardCategoryDocumentInput[];
}

/** The module's one list of what its writes touch — read by every History Logs surface. */
export const AWARD_CATEGORY_AUDIT_ENTITY_TYPES = ['AwardCategory', 'AwardCategoryDocument'];

// --- Awards ---------------------------------------------------------------------------------

export interface AwardListItem {
  id: string;
  referenceNumber: string;
  awardCategoryId: string;
  categoryName: string;
  /** Live nominees registered for it. */
  nomineeCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AwardDetail extends AwardListItem {
  description?: string | null;
  /** The category's live checklist — what a nominee registered NOW will be asked for. */
  checklist: AwardCategoryDocument[];
  /** What the nominees are scored on — the documents first, then the award's own. */
  criteria: AwardCriterion[];
  /** The sum of every criterion's maximum. */
  maxTotal: number;
  /** True once any evaluator has scored — the criteria are fixed from then on. */
  isScoringStarted: boolean;
  /** The employees with a LIVE nomination on this award — left out of the nominate picker. */
  nominatedPersonnelIds: string[];

  // --- The STAGE GATES. Evaluation's values — zero, absent or empty on Nomination without `award-evaluations.read`.
  /** How many winners the deliberation may name — 1 for a single winner. */
  winnerCount: number;
  qualifiedCount?: number;
  disqualifiedCount?: number;
  untaggedCount?: number;
  /** Qualified nominees with at least one sheet — Deliberation opens when it equals `qualifiedCount`. */
  scoredQualifiedCount?: number;
  deliberatedAt?: string | null;
  deliberatedByName?: string | null;
  deliberationRemarks?: string | null;
  awardedAt?: string | null;
  awardedByName?: string | null;
  /** A CALENDAR date (`YYYY-MM-DD`) — `formatDateOnly`, never the instant path. */
  awardedOn?: string | null;
  awardingRemarks?: string | null;
  stageDocuments?: AwardStageDocument[];
}

/** The two stages that keep their own papers. NUMERIC on the wire, mirroring the backend enum. */
export const AWARD_STAGE = { Deliberation: 1, FinalAwarding: 2 } as const;
export type AwardStageValue = (typeof AWARD_STAGE)[keyof typeof AWARD_STAGE];

export interface AwardStageDocument {
  id: string;
  stage: AwardStageValue;
  fileName: string;
  /** The VERIFIED media type off the stored file — what the preview keys on. */
  contentType?: string | null;
  sizeBytes?: number | null;
  createdAt: string;
  createdByName?: string | null;
}

/** The four STAGE GATES, in order — the Evaluation rail. The value is the list's `stage` filter. */
export type AwardStageGate = 'qualification' | 'evaluation' | 'deliberation' | 'awarding';

export interface QualifyNomineeRequest {
  /** `bool?` — saying nothing asserts neither. */
  isQualified: boolean | null;
  remarks: string | null;
}

export interface AwardDeliberationRequest {
  winnerNomineeIds: string[];
  remarks: string | null;
}

export interface AwardFinalAwardingRequest {
  awardedNomineeIds: string[];
  /** `YYYY-MM-DD`. */
  awardedOn: string | null;
  remarks: string | null;
}

/** One thing an award's nominees are scored on. A DOCUMENT criterion is named by its document. */
export interface AwardCriterion {
  id: string;
  /** The checklist document it scores; absent for one of the award's own. Read with `!= null`. */
  awardCategoryDocumentId?: string | null;
  name: string;
  description?: string | null;
  maxPoints: number;
  sortOrder: number;
}

/**
 * One criterion line on the wire. A DOCUMENT line names its document and carries points; an OWN line
 * has no document id, and `id` names one already on the award (null adds). Every row on screen is sent.
 */
export interface AwardCriterionInput {
  id: string | null;
  awardCategoryDocumentId: string | null;
  name: string | null;
  description: string | null;
  maxPoints: number | null;
}

/** Create — the award and its criteria in one save. */
export interface AwardCreateRequest extends AwardRequest {
  criteria: AwardCriterionInput[];
}

/** One evaluator's score sheet for one nominee. */
export interface AwardScoreSheet {
  evaluatorUserId: string;
  /** The account's display name — an alias, never a legal name. */
  evaluatorName?: string | null;
  /** Whether this sheet is the CALLER's own. */
  isMine: boolean;
  total: number;
  lines: { awardCriterionId: string; points: number }[];
  scoredAt: string;
}

/** The caller's own sheet — a line for EVERY criterion. */
export interface SaveAwardScoreRequest {
  lines: { awardCriterionId: string; points: number | null }[];
}

export interface AwardListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  category?: string;
}

/** Create and edit. The number and the counts are the server's; the category is fixed once created. */
export interface AwardRequest {
  awardCategoryId: string | null;
  description: string | null;
  /** 1 for a single winner, more for a top N. Fixed once the deliberation is recorded. */
  winnerCount: number | null;
}

// --- Nominees -------------------------------------------------------------------------------

/** One nominee row. It carries the employee's personId and NO name. */
export interface AwardNomineeListItem {
  id: string;
  awardId: string;
  personnelId: string;
  /** Omitted from the wire where the employee is above the reader's clearance — read with `!= null`. */
  personId?: string | null;
  employeeNumber?: string | null;
  remarks?: string | null;
  documentCount: number;
  /** Lines with a file submitted. */
  submittedCount: number;
  /** The average of the evaluators' totals — absent while unscored, or stripped without `award-evaluations.read`. */
  averageScore?: number | null;
  evaluatorCount?: number | null;
  /** Place among the award's live nominees, ties sharing. Absent while unscored. */
  rank?: number | null;
  /** Requirements qualification — absent until tagged. */
  isQualified?: boolean | null;
  qualificationRemarks?: string | null;
  isWinner?: boolean;
  isAwarded?: boolean;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AwardNomineeDocument {
  id: string;
  awardCategoryDocumentId: string;
  name: string;
  description?: string | null;
  fileName?: string | null;
  /** The stored file's media type, VERIFIED by magic bytes on write — what the preview keys on. */
  contentType?: string | null;
  sizeBytes?: number | null;
  submittedAt?: string | null;
  submittedByName?: string | null;
}

export interface AwardNomineeDetail {
  id: string;
  awardId: string;
  awardReferenceNumber: string;
  categoryName: string;
  /** False while the AWARD is deleted — its nominees then take no write. */
  awardIsActive: boolean;
  personnelId: string;
  personId?: string | null;
  employeeNumber?: string | null;
  remarks?: string | null;
  /** The checklist as it was asked of this nominee. */
  documents: AwardNomineeDocument[];
  /** The award's criteria — the score sheet's lines. */
  criteria: AwardCriterion[];
  maxTotal: number;
  /** Every evaluator's sheet, the caller's first. Empty without `award-evaluations.read`. */
  scoreSheets: AwardScoreSheet[];
  averageScore?: number | null;
  isQualified?: boolean | null;
  qualificationRemarks?: string | null;
  qualifiedAt?: string | null;
  qualifiedByName?: string | null;
  isWinner?: boolean;
  isAwarded?: boolean;
  /** The deliberation is recorded — qualification and scoring are fixed. */
  isDeliberated?: boolean;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AwardNomineeListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  includeDeleted?: boolean;
  /** The stage gate the list is read at. Absent reads every nominee. */
  stage?: AwardStageGate;
}

/** Register and edit. On an edit the employee is the one already nominated. */
export interface AwardNomineeRequest {
  personnelId: string | null;
  remarks: string | null;
}

/** What one award's History Logs reads — the award and every write under it. */
export const AWARD_AUDIT_ENTITY_TYPES = ['Award', 'AwardCriterion', 'AwardNominee', 'AwardNomineeDocument', 'AwardNomineeScore'];
